import userApi from '@/app/features/admin/user/user-service';
import { User } from '@/app/shared/types/models/user.interface';
import { defineStore } from 'pinia';
import { TabHeader } from '@/app/shared/types/tab-header.interface';
import { PaginatedResponse } from '@/app/shared/types/api/api-response.interface';
import { UserDetailPage } from './user-detail.page.interface';

export const useUserStore = defineStore({
  id: 'user-store-id',
  state: () => ({
    userDetails: new Map<string | number, UserDetailPage>(),
    user: {} as User,
    userList: {} as PaginatedResponse<User>,
    tabs: [{ id: 0, name: 'All Users' }] as TabHeader[],
    selectedTab: 0,
  }),
  getters: {
    getUserById: (state) => (id: string | number) => {
      return state.userDetails.get(id);
    },
  },
  actions: {
    async fetchUsers(
      page = 0,
      size = 20,
      sortBy = 'id',
      sortDir: 'asc' | 'desc' = 'asc'
    ) {
      const {
        data: { data },
      } = await userApi.getUsers({ page, size, sortBy, sortDir });
      this.userList = data;
    },

    async createUserTab(detailPage: UserDetailPage) {
      if (detailPage.isNew) {
        detailPage.userData = {
          login: '',
          emailAddress: '',
          notes: '',
          firstName: '',
          lastName: '',
          roles: [],
          superUser: false,
          canChangePassword: false,
          forcePasswordChange: false,
        };
        this.userDetails.set(detailPage.id, detailPage);
      } else {
        const resp = await userApi.getUserById(detailPage.id);
        detailPage.userData = resp.data.data;
        this.userDetails.set(detailPage.id, detailPage);
      }
      const tabIndex = this.tabs.findIndex(
        (tab: TabHeader) => tab.name === detailPage.name
      );
      if (tabIndex === -1) {
        this.tabs.push({
          id: detailPage.id,
          name: detailPage.name,
          autoLocked: false,
        });
        this.selectedTab = this.tabs.length - 1;
      } else {
        this.selectedTab = tabIndex;
      }
    },

    removeTab(selectedId: number) {
      const fIndex = this.tabs.findIndex((tab: any) => tab.id === selectedId);
      fIndex > 0 && this.tabs.splice(fIndex, 1);
    },

    closeTab(tabId: number | string) {
      const tabIndex = this.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex !== -1 && tabIndex !== 0) {
        this.tabs.splice(tabIndex, 1);
        this.userDetails.delete(tabId);
        if (this.selectedTab >= this.tabs.length) {
          this.selectedTab = this.tabs.length - 1;
        }
      }
    },

    selectTab(index: number) {
      this.selectedTab = index;
    },

    async deleteUser(id: number | string) {
      await userApi.deleteUser(id);
      await this.fetchUsers();
    },

    async createUser(userData: any) {
      const response = await userApi.createUser(userData);
      await this.fetchUsers();
      return response.data.data;
    },
    async updateUser(id: number | string, userData: any) {
      const response = await userApi.updateUser(id, userData);
      const detailPage = this.userDetails.get(id);
      if (detailPage) {
        detailPage.userData = response.data.data;
      }
      await this.fetchUsers();
      return response.data.data;
    },
  },
});
